<script setup>
import { ref } from 'vue'
import { Clock, Users } from 'lucide-vue-next'

const STAR_ICON_URL = 'https://sm.naughtycdn.com/images/landers/prize/NA-Star.png'

const props = defineProps({
    casting: { type: Object, required: true },
    routePrefix: { type: String, default: 'members2' },
    // 'requested' shows status + expiry + stars received
    // 'backed'    shows "You gave X stars"
    mode: { type: String, default: 'requested' },
})

const formatNumber = (n) => n ? Number(n).toLocaleString('en-US') : '0'

const formatCastingExpiry = (isoDate) => {
    if (!isoDate) return null
    const diff = new Date(isoDate) - Date.now()
    if (diff <= 0) return 'Expired'
    const days = Math.floor(diff / 86400000)
    if (days > 0) return `${days}d left`
    return `${Math.floor(diff / 3600000)}h left`
}

const castingStatusLabel = (status) => {
    const map = { 0: 'Draft', 1: 'Open', 2: 'Talent Tagged', 3: 'Expired', 4: 'In Pre-production', 5: 'Released' }
    return map[status] ?? String(status)
}

const castingStatusClass = (status) => {
    if (status === 5) return 'text-blue-400 bg-blue-500/10 border-blue-500/20'   // Released
    if (status === 4) return 'text-green-400 bg-green-500/10 border-green-500/20' // ConvertedToAudition
    if (status === 3) return 'text-white/30 bg-white/5 border-white/10'           // Expired
    if (status === 2) return 'text-purple-400 bg-purple-500/10 border-purple-500/20' // TalentTagged
    if (status === 1) return 'text-amber-400 bg-amber-500/10 border-amber-500/20' // Open
    return 'text-white/50 bg-white/5 border-white/10' // Draft (0) + fallback
}

const descriptionExpanded = ref(false)

const timeAgo = (dateStr) => {
    if (!dateStr) return ''
    const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000)
    if (diff < 60)      return 'just now'
    if (diff < 3600)    return `${Math.floor(diff / 60)}m ago`
    if (diff < 86400)   return `${Math.floor(diff / 3600)}h ago`
    if (diff < 2592000) return `${Math.floor(diff / 86400)}d ago`
    return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}
</script>

<template>
    <div
        class="rounded-xl border transition-colors p-4 flex flex-col gap-3"
        :class="casting.status === 3
            ? 'bg-zinc-900/30 border-white/[0.04] opacity-60 grayscale-[40%]'
            : 'bg-zinc-900/60 border-white/[0.06] hover:border-white/[0.12]'"
    >

        <!-- Performers -->
        <template v-if="casting.performers.length === 1">
            <div class="flex items-center gap-3">
                <a
                    :href="casting.performers[0].slug ? route(`${routePrefix}.performer.show`, { slug: casting.performers[0].slug }) : null"
                    class="w-12 h-12 rounded-full overflow-hidden bg-zinc-800 ring-2 ring-zinc-900 flex-shrink-0 hover:ring-white/30 transition-all"
                >
                    <img :src="casting.performers[0].thumbnail_url" :alt="casting.performers[0].name" class="w-full h-full object-cover object-top" loading="lazy" />
                </a>
                <div class="min-w-0">
                    <h3 class="text-white text-sm font-semibold leading-snug line-clamp-2">{{ casting.title }}</h3>
                    <p class="text-white/40 text-xs mt-0.5 truncate">{{ casting.performers[0].name }}</p>
                </div>
            </div>
        </template>

        <template v-else-if="casting.performers.length > 1">
            <div class="flex gap-2 flex-wrap">
                <a
                    v-for="performer in casting.performers.slice(0, 4)"
                    :key="performer.id"
                    :href="performer.slug ? route(`${routePrefix}.performer.show`, { slug: performer.slug }) : null"
                    class="w-9 h-9 rounded-full overflow-hidden bg-zinc-800 ring-2 ring-zinc-900 flex-shrink-0 hover:ring-white/30 transition-all"
                >
                    <img :src="performer.thumbnail_url" :alt="performer.name" class="w-full h-full object-cover object-top" loading="lazy" />
                </a>
                <div v-if="casting.performers.length > 4" class="w-9 h-9 rounded-full bg-zinc-800 ring-2 ring-zinc-900 flex items-center justify-center flex-shrink-0">
                    <span class="text-white/50 text-xs font-bold">+{{ casting.performers.length - 4 }}</span>
                </div>
            </div>
            <div>
                <h3 class="text-white text-sm font-semibold leading-snug line-clamp-2">{{ casting.title }}</h3>
                <p class="text-white/40 text-xs mt-0.5">{{ casting.performers.map(p => p.name).join(', ') }}</p>
            </div>
        </template>

        <!-- No performers - show performer description -->
        <template v-else>
            <div>
                <h3 class="text-white text-sm font-semibold leading-snug line-clamp-2">{{ casting.title }}</h3>
                <p v-if="casting.performer_description" class="text-white/40 text-xs mt-0.5 line-clamp-2">{{ casting.performer_description }}</p>
            </div>
        </template>

        <!-- Description -->
        <p v-if="casting.description" class="text-white/50 text-xs leading-relaxed">
            {{ descriptionExpanded ? casting.description : casting.description.slice(0, 200) }}{{ !descriptionExpanded && casting.description.length > 200 ? '… ' : ' ' }}<button
                v-if="casting.description.length > 200"
                @click="descriptionExpanded = !descriptionExpanded"
                class="text-sky-400/80 hover:text-sky-300 text-xs font-semibold transition-colors"
            >{{ descriptionExpanded ? 'Read less' : 'Read more' }}</button>
        </p>

        <!-- Director's Notes -->
        <div v-if="casting.director_notes" class="px-3 py-2.5 rounded-xl bg-white/[0.03] border border-white/[0.06]">
            <p class="text-white/30 text-[10px] font-bold uppercase tracking-wider mb-1">Director's Notes</p>
            <p class="text-white/50 text-xs leading-relaxed line-clamp-2">{{ casting.director_notes }}</p>
        </div>

        <!-- Format / Setting / Tags pills -->
        <div v-if="casting.format || casting.setting?.length || casting.tags?.length" class="flex flex-wrap items-center gap-1.5">
            <span
                v-if="casting.format"
                class="px-2 py-0.5 rounded-full bg-indigo-500/20 text-indigo-300 border border-indigo-500/30 text-[11px] font-semibold"
            >{{ casting.format }}</span>
            <span
                v-for="s in (casting.setting || [])"
                :key="'s-' + s"
                class="px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-300 text-[11px] font-medium"
            >{{ s }}</span>
            <span
                v-for="tag in (casting.tags || [])"
                :key="'t-' + (tag.id ?? tag)"
                class="px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-300 text-[11px] font-medium"
            >{{ tag.name ?? tag }}</span>
        </div>

        <!-- Progress bar -->
        <div v-if="casting.star_goal > 0" class="pt-2 border-t border-white/[0.06]">
            <div class="flex items-center justify-between mb-1.5">
                <span v-if="casting.stars_received / casting.star_goal >= 0.75" class="text-[11px] font-semibold text-orange-400">🔥 Trending toward production</span>
                <span v-else class="text-[11px] text-white/30"></span>
                <span class="text-[11px] text-white/40 font-medium">{{ formatNumber(casting.stars_received) }} / {{ formatNumber(casting.star_goal) }} stars</span>
            </div>
            <div class="h-1.5 bg-white/[0.08] rounded-full overflow-hidden">
                <div
                    class="h-full bg-gradient-to-r from-orange-500 to-red-500 rounded-full transition-all duration-500"
                    :style="{ width: Math.min(100, Math.round(casting.stars_received / casting.star_goal * 100)) + '%' }"
                />
            </div>
        </div>

        <!-- Footer -->
        <div class="flex items-center justify-between gap-2">
            <div class="flex flex-wrap gap-1.5">
                <!-- requested-only badges -->
                <template v-if="mode === 'requested'">
                    <span
                        v-if="casting.expires_at && casting.status !== 4"
                        class="px-2 py-0.5 rounded-full bg-white/[0.08] text-white/60 text-[11px] font-medium flex items-center gap-1"
                    >
                        <Clock class="w-3 h-3" />
                        {{ formatCastingExpiry(casting.expires_at) }}
                    </span>
                    <span :class="['px-2 py-0.5 rounded-full border text-[11px] font-medium', castingStatusClass(casting.status)]">
                        {{ castingStatusLabel(casting.status) }}
                    </span>
                </template>

                <span
                    v-if="casting.backers_count > 0"
                    class="px-2 py-0.5 rounded-full bg-white/[0.08] text-white/60 text-[11px] font-medium flex items-center gap-1"
                >
                    <Users class="w-3 h-3" />
                    {{ casting.backers_count }} supporter{{ casting.backers_count !== 1 ? 's' : '' }}
                </span>

                <!-- Revised by Staff badge -->
                <span
                    v-if="casting.revised_by_admin"
                    class="px-2 py-0.5 rounded-full bg-emerald-600/90 text-white border border-emerald-400/30 text-[11px] font-bold"
                >
                    Revised by Staff
                </span>
            </div>

            <!-- requested: stars received -->
            <div v-if="mode === 'requested'" class="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-bold bg-amber-500/20 text-amber-400 flex-shrink-0">
                <img :src="STAR_ICON_URL" alt="" class="w-3.5 h-3.5" />
                {{ formatNumber(casting.stars_received) }}
            </div>

            <!-- backed: stars given -->
            <div v-else class="flex items-center gap-2 ml-auto">
                <span class="text-white/30 text-[11px]">You gave</span>
                <span class="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-bold bg-amber-500/20 text-amber-400">
                    <img :src="STAR_ICON_URL" alt="" class="w-3.5 h-3.5" />
                    {{ formatNumber(casting.stars_given) }}
                </span>
            </div>
        </div>

        <p class="text-white/20 text-[11px]">{{ timeAgo(casting.created_at) }}</p>
    </div>
</template>
